import FocusTrap from "focus-trap-react";
import { Global, Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { FadeIn, FlexRow, H2, IconClose, Link, Text } from "atoms";
import { constants } from "config";

interface ForbiddenModalProps {
  isShowing: boolean;
}

const globalCSS = (theme: Theme) => ({
  "html,body": {
    background: theme.colors.primary,
    height: "100vh",
    overflow: "hidden",
  },
  "main::after": {
    background: `${theme.gradients.radial1} !important`,
  },
});

const focusTrapOptions = {
  initialFocus: "p + a",
  allowOutsideClick: (event: MouseEvent | TouchEvent) => {
    if (
      event.target instanceof Element &&
      (event.target.className.includes("SubMenuLink") ||
        event.target.parentElement?.className.includes("LogoLink"))
    ) {
      return true;
    }
    return false;
  },
};

function ForbiddenModal({ isShowing }: ForbiddenModalProps) {
  if (!isShowing) return null;

  return (
    <div>
      <Global styles={globalCSS} />

      <ModalOverlay />
      <FocusTrap focusTrapOptions={focusTrapOptions}>
        <ModalContainer role="alert">
          <Close to="/">
            <IconClose />
          </Close>

          <ModalContent>
            <div>
              <SH2 size="h1">Thanks for visiting!</SH2>

              <SText1 size="lg">
                You must be a HUG Pass holder
                to&nbsp;view&nbsp;this&nbsp;project.
              </SText1>
            </div>

            <SFlexRow flexWrap="wrap">
              {/* TODO: If signed in but not a pass holder, hide the Sign In button */}
              <div>
                <Text size="xs">Already a pass holder?</Text>
                <SLink to="/sign-in" variant="gradient">
                  Sign In
                </SLink>
              </div>

              <div>
                <Text size="xs">Need a HUG Pass?</Text>
                <SLink href={constants.openseaURL} variant="gradient">
                  Buy on OpenSea
                </SLink>
              </div>
            </SFlexRow>
          </ModalContent>
        </ModalContainer>
      </FocusTrap>
    </div>
  );
}

// Prevent clicks on content below modal
const ModalOverlay = styled.div({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 299,
});

// Allow overflow scroll of modal content within this container
const ModalContainer = styled(FadeIn)(({ theme }) => ({
  background: theme.colors.bgLight,
  bottom: 0,
  left: 0,
  position: "fixed",
  right: 0,
  textAlign: "center",
  top: 0,
  zIndex: 500,
  "@media (min-width: 400px)": {
    border: `1px solid ${theme.colors.fg}`,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.boxShadow.dark,
    height: 575,
    left: "50%",
    maxHeight: "calc(100% - 4rem)",
    maxWidth: "calc(100% - 4rem)",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
  },
}));

ModalContainer.defaultProps = {
  duration: 1000,
  delay: 500,
};

const modalPadding = "calc(.5rem + 2vw)";

const Close = styled(Link)(({ theme }) => ({
  alignItems: "center",
  borderRadius: theme.borderRadius.round,
  display: "flex",
  height: `calc(${modalPadding} * 2)`,
  justifyContent: "center",
  right: `calc(${modalPadding} / 2)`,
  position: "absolute",
  top: `calc(${modalPadding} / 2)`,
  width: `calc(${modalPadding} * 2)`,
  zIndex: 301,
}));

const ModalContent = styled.div({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "calc(2rem + 2vmin)",
  height: "100%",
  justifyContent: "center",
  left: 0,
  overflow: "auto",
  padding: modalPadding,
  position: "absolute",
  width: "100%",
});

const SH2 = styled(H2)({
  marginBottom: "1.5rem",
});

const SFlexRow = styled(FlexRow)({
  gap: "2rem",
  "&>div": {
    flex: "0 0 100%",
  },
  p: {
    whiteSpace: "nowrap",
  },
  a: {
    minWidth: "10em",
  },
  "@media (min-width: 440px)": {
    gap: "1.5rem",
    "&>div": {
      flex: "1",
    },
  },
});

const SText1 = styled(Text)(({ theme }) => ({
  ...theme.fonts.display,
  fontSize: theme.fontSizes.h2,
  marginBottom: "1.5rem",
  maxWidth: 660,
}));

const SLink = styled(Link)({
  justifyContent: "center",
  whiteSpace: "nowrap",
  svg: {
    marginLeft: "0.8rem",
    height: "26px",
  },
});

export { ForbiddenModal };
